import styled from "styled-components";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import { faArrowRight } from "@fortawesome/free-solid-svg-icons";

import { isSignInWithEmailLink } from "firebase/auth";
import { fireAuth } from "../../Firebase/firebase";

import ConditionsSlideIn from "../../Components/ConditionsSlideIn";
import SignInBoxEmail from "./ComponentsAndUtils/SignInBox/SignInBoxEmail";
import SignInBoxPhone from "./ComponentsAndUtils/SignInBox/SignInBoxPhone";
import ActionButton from "./ComponentsAndUtils/ActionButton";
import { desktopMarginsCutoff, mobileMarginVal } from "../../styleConstants";

const Wrapper = styled.div`
  padding-top: ${(props) => (props.$useMobileMargins ? "16px" : "34px")};
  padding-bottom: ${(props) => (props.$useMobileMargins ? "24px" : "36px")};
  overflow: ${(props) => (props.$stopScroll ? "hidden" : "auto")};
  position: ${(props) => (props.$stopScroll ? "fixed" : "")};

  // just eye-balled what is a good page width.
  // for mobile views, I want to set a max width to the screen width minus mobile margins.
  // This is to keep things within margins even when a slide in is open.
  max-width: min(680px, calc(100vw - ${mobileMarginVal * 2}px));
  // max-width: 70ch;
`;

const DoubleButtonWrapper = styled.div`
  margin-top: 24px;
  margin-bottom: 12px;
  display: flex;
  max-width: 432px;
`;

const ButtonSpace = styled.div`
  min-width: 8px;
`;

const TermsAgreement = styled.div`
  color: rgba(0, 0, 0, 0.5);
  font-size: 14px;

  @media screen and (min-width: ${desktopMarginsCutoff}px) {
    margin-top: 4px;
  }
`;

const SignInHome = () => {
  const navigate = useNavigate();
  const screenWidth = useSelector((state) => state.screenWidth.value);
  const useMobileMargins = screenWidth < 540;

  // navigate to account if we are already signed in with a selected/matched voter.
  const signedInUser = useSelector((state) => state.user.value);
  useEffect(() => {
    if (signedInUser) {
      navigate("/account");
    }
  }, [signedInUser]);

  const [signInConditionsOpen, setSignInConditionsOpen] = useState(false);
  const [signInMethod, setSignInMethod] = useState(null);
  const [fromEmailRedirect, setFromEmailRedirect] = useState(false); // if this is true, we have to show the email sign in box to complete sign in.

  // in the case that we've set the recaptcha verifier but we cancelled the phone auth attempt, refresh the page once to clear window.recaptchaVerifier
  const recaptchaEffectMounted = useRef(false);
  useEffect(() => {
    // this is Claude code's suggested fix to the page being janky on a new account phone signin.
    // It wants to avoid a page reload in this case.
    if (!recaptchaEffectMounted.current) {
      recaptchaEffectMounted.current = true;
      return;
    }
    if (window.recaptchaVerifier) {
      window.location.reload(false);
    }
  }, [signInMethod]);

  // set flag if we came here from an email sign-in link
  useEffect(() => {
    if (isSignInWithEmailLink(fireAuth, window.location.href)) {
      setFromEmailRedirect(true);
    } else {
      setFromEmailRedirect(false);
    }
  }, [window.location.href]);

  // either the phone or email sign in box that we show.
  let boxToShow = <></>;
  if (signInMethod === "phone") {
    boxToShow = (
      <SignInBoxPhone
        onCancel={() => {
          setSignInMethod(null);
        }}
      />
    );
  } else if (signInMethod === "email" || fromEmailRedirect) {
    boxToShow = (
      <SignInBoxEmail
        fromEmailRedirect={fromEmailRedirect}
        onCancel={() => {
          setSignInMethod(null);
        }}
      />
    );
  }

  return (
    <>
      {signInConditionsOpen && (
        <ConditionsSlideIn
          closeConditions={() => {
            setSignInConditionsOpen(false);
          }}
        />
      )}
      <Wrapper
        $useMobileMargins={useMobileMargins}
        $stopScroll={signInConditionsOpen}
      >
        {/* if from email redirect, we need to show the email sign in box, not this other stuff. */}
        {!signInMethod && !fromEmailRedirect && (
          <>
            Sign in with your phone number or email address.
            <DoubleButtonWrapper>
              <ActionButton
                isSingleAction={false}
                text={"VERIFY PHONE"}
                onClick={() => {
                  setSignInMethod("phone");
                }}
                faIconRight={faArrowRight}
              />
              <ButtonSpace />
              <ActionButton
                isSingleAction={false}
                text={"VERIFY EMAIL"}
                onClick={() => {
                  setSignInMethod("email");
                }}
                faIconRight={faArrowRight}
              />
            </DoubleButtonWrapper>
            <TermsAgreement>
              By signing in, you agree to our{" "}
              <Link
                to="/about/terms-of-service"
                target="_blank"
                style={{ color: "rgba(0, 0, 0, 0.5)" }}
              >
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link
                to="/about/privacy-policy"
                target="_blank"
                style={{ color: "rgba(0, 0, 0, 0.5)" }}
              >
                Privacy Policy
              </Link>
              .
            </TermsAgreement>
          </>
        )}
        {(signInMethod || fromEmailRedirect) && boxToShow}
      </Wrapper>
    </>
  );
};

export default SignInHome;
